import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent, ReactNode } from 'react';
import { AutocompleteList } from './lib/AutocompleteList';
import { filterItems, limitItems, normalizeHighlightedIndex } from './lib/helpers';
import type { AutocompleteProps, RenderFurtherMessageItem, RenderItem, RenderNoResults } from './types';

export const Autocomplete = <TItem,>(props: AutocompleteProps<TItem>) => {
  const {
    value,
    placeholder,
    items,
    limit,
    styleList,
    getItemValue: getItemValueProp,
    getItemKey,
    shouldItemRender,
    onInput: onInputProp,
    onSelect: onSelectProp,
    onUnselectedEnter,
    onFurtherSelect: onFurtherSelectProp,
    renderItem: renderItemProp,
    renderFurtherMessageItem: renderFurtherMessageItemProp,
    renderNoResults: renderNoResultsProp,
  } = props;

  const [hasFocus, setHasFocus] = useState(false);
  const [listIsVisible, setListIsVisible] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const renderItemsRef = useRef<TItem[]>([]);
  const hasFurtherMessageRef = useRef(false);
  const highlightedIndexRef = useRef(highlightedIndex);

  // Keep a ref mirror of the highlighted index so the stable event handlers can
  // read the latest value without being recreated on every change.
  useEffect(() => {
    highlightedIndexRef.current = highlightedIndex;
  }, [highlightedIndex]);

  const updateHighlighting = useCallback(
    (index?: number | null, useRenderItems?: TItem[]) => {
      const useIndex = index !== null && index !== undefined ? index : highlightedIndexRef.current;
      const list = useRenderItems ?? renderItemsRef.current;
      const normalized = normalizeHighlightedIndex(useIndex, list.length, hasFurtherMessageRef.current, limit);

      if (normalized !== highlightedIndexRef.current) {
        setHighlightedIndex(normalized);
      }
      return normalized;
    },
    [limit],
  );

  // Recompute the rendered/limited items whenever inputs change and re-clamp the
  // highlighted index. Mirrors refs so handlers stay in sync even while hidden.
  useEffect(() => {
    const filtered = filterItems(items ?? [], value ?? '', shouldItemRender);
    const { renderItems, renderFurtherMessage } = limitItems(filtered, limit);
    hasFurtherMessageRef.current = renderFurtherMessage;
    renderItemsRef.current = renderItems;
    updateHighlighting(null, renderItems);
  }, [items, value, shouldItemRender, limit, updateHighlighting]);

  // Clear the highlight when the mouse moves outside the component.
  useEffect(() => {
    const globalMouseMove = (e: MouseEvent) => {
      if (!containerRef.current || highlightedIndexRef.current <= -1) {
        return;
      }
      if (e.target === containerRef.current || containerRef.current.contains(e.target as Node)) {
        return;
      }
      setHighlightedIndex(-1);
    };

    document.addEventListener('mousemove', globalMouseMove);
    return () => document.removeEventListener('mousemove', globalMouseMove);
  }, []);

  const setFocus = (focus: boolean) => {
    setHasFocus(focus);
    setListIsVisible(focus);
  };

  const getItemValue = (item: TItem): string => (typeof getItemValueProp === 'function' ? getItemValueProp(item) : (item as unknown as string));

  const onFurtherSelect = () => {
    if (onFurtherSelectProp) {
      onFurtherSelectProp();
    }
  };

  const onSelect = (selectValue?: string, isFurther = false) => {
    if (typeof onSelectProp !== 'function') {
      return;
    }

    if (selectValue && !isFurther) {
      return onSelectProp(selectValue);
    }

    if (!selectValue && isFurther) {
      return onFurtherSelect();
    }

    const highlightedItem = renderItemsRef.current[highlightedIndexRef.current];

    if (!highlightedItem && highlightedIndexRef.current > 0) {
      return onFurtherSelect();
    }

    if (highlightedItem) {
      setListIsVisible(false);
      return onSelectProp(getItemValue(highlightedItem));
    }

    return false;
  };

  const onEnter = () => {
    const selectResult = onSelect();
    if (selectResult === false && typeof onUnselectedEnter === 'function') {
      onUnselectedEnter();
    }
  };

  const moveHighlighting = (direction: 1 | -1) => {
    updateHighlighting(highlightedIndexRef.current + direction);
  };

  const onInput = (e: FormEvent<HTMLInputElement>) => {
    if (!listIsVisible) {
      setListIsVisible(true);
    }

    if (!onInputProp) {
      return;
    }
    onInputProp(e, (e.target as HTMLInputElement).value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'Down':
        e.preventDefault();
        moveHighlighting(1);
        break;
      case 'ArrowUp':
      case 'Up':
        e.preventDefault();
        moveHighlighting(-1);
        break;
      default:
        return true;
    }
  };

  const onKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        onEnter();
        break;
      case 'Escape':
        setListIsVisible(false);
        break;
      default:
        return true;
    }
  };

  const renderItem: RenderItem<TItem> = renderItemProp ?? ((item) => ((item as { label?: ReactNode }).label ? (item as { label?: ReactNode }).label : (item as ReactNode)));
  const renderFurtherMessageItem: RenderFurtherMessageItem = renderFurtherMessageItemProp ?? (() => '...');
  const renderNoResults: RenderNoResults = renderNoResultsProp ?? (() => <li>No results</li>);

  let listNode: ReactNode = null;
  if (value && hasFocus && listIsVisible) {
    const filtered = filterItems(items ?? [], value, shouldItemRender);
    const { renderItems, renderFurtherMessage } = limitItems(filtered, limit);

    // The keyboard/mouse handlers read these refs, so keep them current with
    // what is actually on screen (preserves original render-time mutation).
    hasFurtherMessageRef.current = renderFurtherMessage;
    renderItemsRef.current = renderItems;

    listNode = (
      <AutocompleteList
        renderItems={renderItems}
        hasFurtherMessage={renderFurtherMessage}
        highlightedIndex={highlightedIndex}
        styleList={styleList}
        getItemKey={getItemKey}
        getItemValue={getItemValue}
        renderItem={renderItem}
        renderFurtherMessageItem={renderFurtherMessageItem}
        renderNoResults={renderNoResults}
        onSelect={onSelect}
        onHighlight={(index) => updateHighlighting(index)}
      />
    );
  }

  return (
    <div ref={containerRef} className="Autocomplete relative">
      <input onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} onKeyDown={onKeyDown} onKeyUp={onKeyUp} onInput={onInput} type="text" value={value ?? ''} placeholder={placeholder} />
      {listNode}
    </div>
  );
};
